/**
 * ROUTAGE D'AUTHENTIFICATION (LOT 6) — le câblage volontairement laissé de
 * côté jusqu'ici : sous enforcement, un anonyme est envoyé vers /login et
 * REVIENT où il allait après connexion (`return_to`), une session qui meurt
 * en cours d'usage mène à /session-expired, un 403 franc à /access-denied.
 *
 * RÈGLES return_to (anti open-redirect) :
 *   · uniquement un CHEMIN interne relatif commençant par « / » ;
 *   · jamais « // », jamais de backslash, jamais de schéma ;
 *   · jamais un chemin du monde auth (/login, /session-expired…) — sinon
 *     boucle ;
 *   · transporté via sessionStorage entre les documents du parcours
 *     (login → MFA → portail), consommé UNE fois.
 */

const AUTH_PREFIXES = ['/login', '/session-expired', '/access-denied', '/design/']
const KEY = 'lc-return-to'

/** Valide un return_to : chemin interne sûr, ou null. */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!raw.startsWith('/')) return null
  if (raw.startsWith('//') || raw.includes('\\')) return null
  if (raw.includes('://')) return null
  // AUCUN caractère de contrôle : le navigateur RETIRE tabulations et sauts de
  // ligne d'une adresse, « /\t/evil.example » devenait « //evil.example ».
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null
  if (AUTH_PREFIXES.some((p) => raw === p || raw.startsWith(p))) return null
  if (raw.length > 512) return null
  // Et la preuve : résolue comme le ferait le navigateur, l'adresse reste ICI.
  try {
    if (new URL(raw, 'https://gestion.invalid').origin !== 'https://gestion.invalid') return null
  } catch { return null }
  return raw
}

/** À l'arrivée sur /login : mémorise le return_to du query string (validé). */
export function stashReturnToFromQuery(): void {
  const rt = safeReturnTo(new URLSearchParams(window.location.search).get('return_to'))
  try {
    if (rt) sessionStorage.setItem(KEY, rt)
  } catch { /* stockage indisponible : on retombera sur l'accueil */ }
}

/** À la fin du parcours de connexion : destination = return_to consommé,
 *  sinon la destination par défaut (page d'accueil préférée). */
export function consumeReturnTo(fallback: string): string {
  let v: string | null = null
  try {
    v = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
  } catch { /* stockage indisponible */ }
  return safeReturnTo(v) ?? fallback
}

/* ─── état « a été authentifié dans CE document » ───
   Sert à distinguer « jamais connecté » (→ /login) d'une session qui vient
   d'expirer en cours d'usage (→ /session-expired). */
let hadSession = false
export function markAuthenticated(): void { hadSession = true }

let redirecting = false

/** Un seul déclenchement par document — pas de rafale de redirections. */
export function onUnauthenticated(): void {
  if (redirecting) return
  const here = window.location.pathname
  if (AUTH_PREFIXES.some((p) => here === p || here.startsWith(p))) return
  redirecting = true
  const rt = safeReturnTo(here)
  const q = rt && rt !== '/' ? `?return_to=${encodeURIComponent(rt)}` : ''
  window.location.assign(hadSession ? `/session-expired${q}` : `/login${q}`)
}

export function onForbidden(): void {
  if (redirecting) return
  const here = window.location.pathname
  if (AUTH_PREFIXES.some((p) => here === p || here.startsWith(p))) return
  redirecting = true
  window.location.assign('/access-denied')
}

/** Origine non canonique (LOT 6, §102) : quand PUBLIC_ORIGIN est configurée,
 *  le portail refuse de fonctionner ailleurs (hors localhost de banc). */
export function wrongOrigin(publicOrigin: string | null | undefined): boolean {
  if (!publicOrigin) return false
  if (window.location.origin === publicOrigin) return false
  const h = window.location.hostname
  if (h === 'localhost' || h === '127.0.0.1') return false
  return true
}
